import React, { useState, useEffect, useRef } from 'react';
import { Send, Paperclip, DollarSign, MessageSquare, File, ArrowLeft } from 'lucide-react';
import { supabase } from '../supabaseClient';
import './Messagespage.css';
import './AnalysisLayout.css';

function timeAgo(ts) {
    if (!ts) return '';
    const diff = Date.now() - new Date(ts).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return new Date(ts).toLocaleDateString();
}

function Avatar({ name, photoUrl, size = 44 }) {
    const initials = (name || '?').split(' ').filter(Boolean).map(w => w[0]).join('').slice(0, 2).toUpperCase();
    const [imgFailed, setImgFailed] = React.useState(false);

    // Reset error state when photoUrl changes
    React.useEffect(() => { setImgFailed(false); }, [photoUrl]);

    return (
        <div className="msg-avatar" style={{ width: size, height: size, fontSize: Math.max(10, size * 0.35) }}>
            {photoUrl && !imgFailed
                ? <img src={photoUrl} alt={name || 'User'} onError={() => setImgFailed(true)} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                : <span>{initials || '?'}</span>
            }
        </div>
    );
}

function OfferCard({ offer, isClient, onAccept, onDecline }) {
    return (
        <div className="msg-offer-card">
            <div className="msg-offer-card-header"><DollarSign size={14} /> Offer from Lawyer</div>
            <div className="msg-offer-card-body">
                <div className="msg-offer-title">{offer.title}</div>
                {offer.description && <div className="msg-offer-desc">{offer.description}</div>}
                <div className="msg-offer-price">PKR {Number(offer.price).toLocaleString()}</div>
                {offer.status === 'pending' && isClient ? (
                    <div className="msg-offer-btns">
                        <button className="msg-offer-accept" onClick={() => onAccept(offer)}>✓ Accept</button>
                        <button className="msg-offer-decline" onClick={() => onDecline(offer)}>✗ Decline</button>
                    </div>
                ) : (
                    <div className={`msg-offer-status ${offer.status}`}>
                        {offer.status === 'accepted' ? '✓ Offer Accepted — You are now connected!'
                            : offer.status === 'declined' ? '✗ Offer Declined'
                                : '⏳ Awaiting client response'}
                    </div>
                )}
            </div>
        </div>
    );
}

export default function MessagesPage({ user, userRole, initialConversationId }) {
    const [conversations, setConversations] = useState([]);
    // profilesById: { [userId]: { id, full_name, photo_url, email } }
    const [profilesById, setProfilesById] = useState({});
    const [activeConv, setActiveConv] = useState(null);
    const [messages, setMessages] = useState([]);
    const [text, setText] = useState('');
    const [sending, setSending] = useState(false);
    const [showOfferModal, setShowOfferModal] = useState(false);
    const [offerForm, setOfferForm] = useState({ title: '', description: '', price: '' });
    const [search, setSearch] = useState('');
    const [uploading, setUploading] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const bottomRef = useRef(null);
    const fileInputRef = useRef(null);
    const activeConvRef = useRef(null);

    useEffect(() => {
        activeConvRef.current = activeConv;
    }, [activeConv]);

    // Load own profile + all conversations on mount
    useEffect(() => {
        if (!user?.id) return;
        (async () => {
            await loadAll();
            setIsLoading(false);
        })();
    }, [user?.id]);

    // Auto-open conversation when coming from AdvocateList
    useEffect(() => {
        if (!initialConversationId) return;

        const tryOpen = async () => {
            // Try from already-loaded list first
            const found = conversations.find(c => c.id === initialConversationId);
            if (found) { openConversation(found); return; }
            // Otherwise fetch directly
            const { data } = await supabase.from('conversations').select('*').eq('id', initialConversationId).single();
            if (data) openConversation(data);
        };
        tryOpen();
    }, [initialConversationId, conversations.length]);

    const fetchProfilesMap = async (ids) => {
        const uniqueIds = [...new Set((ids || []).filter(Boolean))];
        const map = {};

        if (uniqueIds.length === 0) return map;

        const [{ data: profilesById }, { data: advs }, { data: legacyUsers }] = await Promise.all([
            supabase
                .from('user_profiles')
                .select('id, full_name, photo_url, email')
                .in('id', uniqueIds),
            supabase
                .from('advocates')
                .select('user_id, name, email, photo_path')
                .in('user_id', uniqueIds),
            supabase
                .from('users')
                .select('id, name, email')
                .in('id', uniqueIds)
        ]);

        const mergeProfile = (targetId, incoming) => {
            if (!targetId) return;
            const existing = map[targetId] || { id: targetId, full_name: null, photo_url: null, email: null };
            map[targetId] = {
                id: targetId,
                full_name: existing.full_name || incoming.full_name || null,
                photo_url: existing.photo_url || incoming.photo_url || null,
                email: existing.email || incoming.email || null,
            };
        };

        (profilesById || []).forEach((p) => {
            if (!p?.id) return;
            mergeProfile(p.id, p);
        });

        (advs || []).forEach((a) => {
            mergeProfile(a.user_id, {
                full_name: a.name || null,
                photo_url: a.photo_path || null,
                email: a.email || null,
            });
        });

        (legacyUsers || []).forEach((u) => {
            mergeProfile(u.id, {
                full_name: u.name || null,
                photo_url: null,
                email: u.email || null,
            });
        });

        uniqueIds.forEach((id) => {
            if (!map[id]) map[id] = { id, full_name: null, photo_url: null, email: null };
        });

        return map;
    };

    const loadAll = async () => {
        const col = userRole === 'lawyer' ? 'lawyer_id' : 'client_id';

        // 1. Load conversations
        const { data: convs } = await supabase
            .from('conversations').select('*').eq(col, user.id)
            .order('last_message_at', { ascending: false });

        if (!convs) return;

        // 2. Collect ALL unique user IDs (own + all others)
        const allIds = new Set([user.id]);
        convs.forEach(c => {
            if (c.lawyer_id) allIds.add(c.lawyer_id);
            if (c.client_id) allIds.add(c.client_id);
        });

        const idArr = Array.from(allIds);
        const map = await fetchProfilesMap(idArr);

        // 3. Set both conversations and profiles at the same time
        setConversations(convs);
        setProfilesById(map);
    };

    const openConversation = async (conv) => {
        setActiveConv(conv);

        // Make sure both participants' profiles are loaded
        const missing = [conv.lawyer_id, conv.client_id, user.id].filter(id => id && (!profilesById[id] || !profilesById[id].full_name || !profilesById[id].photo_url));
        if (missing.length > 0) {
            const fetched = await fetchProfilesMap(missing);
            setProfilesById(prev => ({ ...prev, ...fetched }));
        }

        await loadMessages(conv.id);
        // Mark messages as read
        await supabase.from('messages').update({ is_read: true })
            .eq('conversation_id', conv.id).neq('sender_id', user.id);
    };

    const loadMessages = async (convId) => {
        const { data: msgs } = await supabase.from('messages').select('*')
            .eq('conversation_id', convId).order('created_at', { ascending: true });
        const { data: offers } = await supabase.from('offers').select('*')
            .eq('conversation_id', convId).order('created_at', { ascending: true });

        const combined = [
            ...(msgs || []).map(m => ({ ...m, _type: 'message' })),
            ...(offers || []).map(o => ({ ...o, _type: 'offer' })),
        ].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

        setMessages(combined);
        setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    };

    // Real-time updates for currently opened conversation
    useEffect(() => {
        const id = activeConv?.id;
        if (!id) return;

        const channel = supabase.channel(`conv-${id}`)
            .on(
                'postgres_changes',
                { event: 'INSERT', schema: 'public', table: 'messages', filter: `conversation_id=eq.${id}` },
                async (payload) => {
                    await loadMessages(id);
                    await loadAll();
                    if (payload?.new?.sender_id && payload.new.sender_id !== user.id) {
                        await supabase.from('messages').update({ is_read: true }).eq('id', payload.new.id);
                    }
                }
            )
            .on(
                'postgres_changes',
                { event: 'INSERT', schema: 'public', table: 'offers', filter: `conversation_id=eq.${id}` },
                async () => {
                    await loadMessages(id);
                    await loadAll();
                }
            )
            .on(
                'postgres_changes',
                { event: 'UPDATE', schema: 'public', table: 'offers', filter: `conversation_id=eq.${id}` },
                async () => {
                    await loadMessages(id);
                    await loadAll();
                }
            )
            .subscribe();

        return () => supabase.removeChannel(channel);
    }, [activeConv?.id, user?.id]);

    // Keep conversation list fresh while user is on messages page.
    useEffect(() => {
        if (!user?.id) return;

        const refreshConversations = async (payload) => {
            const changed = payload?.new || payload?.old;
            if (!changed) return;

            await loadAll();

            const activeId = activeConvRef.current?.id;
            if (activeId && changed.id === activeId) {
                await loadMessages(activeId);
            }
        };

        const lawyerConvsChannel = supabase.channel(`convs-lawyer-${user.id}`)
            .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'conversations', filter: `lawyer_id=eq.${user.id}` }, refreshConversations)
            .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'conversations', filter: `lawyer_id=eq.${user.id}` }, refreshConversations)
            .subscribe();

        const clientConvsChannel = supabase.channel(`convs-client-${user.id}`)
            .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'conversations', filter: `client_id=eq.${user.id}` }, refreshConversations)
            .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'conversations', filter: `client_id=eq.${user.id}` }, refreshConversations)
            .subscribe();

        return () => {
            supabase.removeChannel(lawyerConvsChannel);
            supabase.removeChannel(clientConvsChannel);
        };
    }, [user?.id]);

    const sendMessage = async (content, fileUrl = null, fileName = null) => {
        if (!activeConv || (!content?.trim() && !fileUrl)) return;
        setSending(true);
        await supabase.from('messages').insert([{
            conversation_id: activeConv.id,
            sender_id: user.id,
            content: content?.trim() || null,
            file_url: fileUrl,
            file_name: fileName,
        }]);
        await supabase.from('conversations').update({ last_message_at: new Date().toISOString() }).eq('id', activeConv.id);
        setText('');
        setSending(false);
        await loadMessages(activeConv.id);
        loadAll();
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file || !activeConv) return;
        setUploading(true);
        try {
            const ext = file.name.split('.').pop();
            const path = `messages/${activeConv.id}/${Date.now()}.${ext}`;
            const { error } = await supabase.storage.from('message-files').upload(path, file, { upsert: true });
            if (error) throw error;
            const { data: { publicUrl } } = supabase.storage.from('message-files').getPublicUrl(path);
            await sendMessage(null, publicUrl, file.name);
        } catch (err) {
            alert('File upload failed: ' + err.message);
        } finally {
            setUploading(false);
            e.target.value = '';
        }
    };

    const sendOffer = async () => {
        if (!offerForm.title || !offerForm.price || !activeConv) return;
        await supabase.from('offers').insert([{
            conversation_id: activeConv.id,
            lawyer_id: activeConv.lawyer_id,
            client_id: activeConv.client_id,
            title: offerForm.title,
            description: offerForm.description,
            price: parseFloat(offerForm.price),
            status: 'pending',
        }]);
        setOfferForm({ title: '', description: '', price: '' });
        setShowOfferModal(false);
        await loadMessages(activeConv.id);
        loadAll();
    };

    const acceptOffer = async (offer) => {
        await supabase.from('offers').update({ status: 'accepted' }).eq('id', offer.id);

        const { data: existing } = await supabase.from('user_relationships')
            .select('id').eq('lawyer_id', offer.lawyer_id).eq('client_id', offer.client_id).maybeSingle();

        if (!existing) {
            await supabase.from('user_relationships').insert([{
                lawyer_id: offer.lawyer_id,
                client_id: offer.client_id,
                status: 'active',
                case_type: offer.title,
                case_details: `Hired via offer: "${offer.title}" at PKR ${offer.price}`,
            }]);
        } else {
            await supabase.from('user_relationships').update({ status: 'active' }).eq('id', existing.id);
        }

        await supabase.from('messages').insert([{
            conversation_id: activeConv.id,
            sender_id: user.id,
            content: `✅ Offer accepted! "${offer.title}" — You are now officially connected as lawyer and client.`,
        }]);
        await supabase.from('conversations').update({ last_message_at: new Date().toISOString() }).eq('id', activeConv.id);
        await loadMessages(activeConv.id);
    };

    const declineOffer = async (offer) => {
        await supabase.from('offers').update({ status: 'declined' }).eq('id', offer.id);
        await supabase.from('messages').insert([{
            conversation_id: activeConv.id,
            sender_id: user.id,
            content: `❌ Offer declined: "${offer.title}"`,
        }]);
        await supabase.from('conversations').update({ last_message_at: new Date().toISOString() }).eq('id', activeConv.id);
        await loadMessages(activeConv.id);
    };

    // Helpers using profilesById
    const getProfile = (id) => profilesById[id] || null;
    const getDisplayName = (id) => {
        const p = profilesById[id];
        if (!p) return 'Loading...';
        if (p.full_name) return p.full_name;
        if (p.email) return p.email.split('@')[0]; // use email prefix as fallback
        return 'User';
    };
    const getPhotoUrl = (id) => profilesById[id]?.photo_url || null;
    const otherUserId = activeConv ? (userRole === 'lawyer' ? activeConv.client_id : activeConv.lawyer_id) : null;
    const otherProfile = otherUserId ? getProfile(otherUserId) : null;
    const myProfile = getProfile(user.id);

    const filteredConvs = conversations.filter(c => {
        if (!search) return true;
        const otherId = userRole === 'lawyer' ? c.client_id : c.lawyer_id;
        const p = getProfile(otherId);
        return (p?.full_name || p?.email || '').toLowerCase().includes(search.toLowerCase());
    });

    return (
        <>
            {isLoading ? (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    backgroundColor: 'var(--color-background)',
                    fontSize: '16px',
                    color: 'var(--color-text-secondary)',
                    fontFamily: 'var(--font-family)',
                }}>
                    <div style={{ textAlign: 'center' }}>
                        <div style={{
                            display: 'inline-block',
                            width: '40px',
                            height: '40px',
                            border: '4px solid var(--color-border-light)',
                            borderTop: '4px solid var(--color-primary)',
                            borderRadius: '50%',
                            animation: 'spin 0.8s linear infinite',
                            marginBottom: '16px',
                        }} />
                        <p>Loading conversations...</p>
                    </div>
                    <style>{`
                        @keyframes spin {
                            0% { transform: rotate(0deg); }
                            100% { transform: rotate(360deg); }
                        }
                    `}</style>
                </div>
            ) : (
                <div className="analysis-page-container messages-page-v2">
                    <div className="analysis-hero">
                        <div className="hero-text-content">
                            <h1>Secure Inbox</h1>
                            <p>Communicate directly with legal experts and manage your case offers in one place.</p>
                        </div>
                        <div className="hero-abstract-decoration">
                            <div className="abstract-circle abstract-circle-1" style={{ background: '#f0f9ff' }}></div>
                            <div className="abstract-circle abstract-circle-2" style={{ background: '#e0f2fe' }}></div>
                            <MessageSquare className="hero-bg-icon" size={160} />
                        </div>
                    </div>

                    <div className="analysis-grid msg-grid">
                        {/* Conversation panel - Main Column */}
                        <div className="analysis-main-col">
                            {activeConv ? (
                                <div className="msg-conversation">
                                    {/* Header */}
                                    <div className="msg-conv-header">
                                        <button className="mobile-back-btn" onClick={() => setActiveConv(null)}>
                                            <ArrowLeft size={20} />
                                        </button>
                                        <Avatar name={otherUserId ? getDisplayName(otherUserId) : 'User'} photoUrl={otherUserId ? getPhotoUrl(otherUserId) : null} size={42} />
                                        <div className="msg-conv-header-info">
                                            <h3>{otherUserId ? getDisplayName(otherUserId) : 'User'}</h3>
                                            <p>{otherProfile?.email || ''}</p>
                                        </div>
                                        <div className="msg-conv-actions">
                                            {userRole === 'lawyer' && (
                                                <button className="msg-offer-btn" onClick={() => setShowOfferModal(true)}>
                                                    <DollarSign size={14} /> Send Offer
                                                </button>
                                            )}
                                        </div>
                                    </div>

                            {/* Messages */}
                            <div className="msg-messages-area">
                                {messages.map(item => {
                                    // Determine sender
                                    const senderId = item._type === 'message' ? item.sender_id : item.lawyer_id;
                                    const isMine = senderId === user.id;
                                    const senderProfile = getProfile(senderId);
                                    const senderName = isMine ? getDisplayName(user.id) : (senderId ? getDisplayName(senderId) : 'User');
                                    const senderPhoto = senderProfile?.photo_url || null;

                                    if (item._type === 'offer') {
                                        return (
                                            <div key={`offer-${item.id}`} className={`msg-bubble-wrap ${isMine ? 'mine' : 'theirs'}`}>
                                                {!isMine && (
                                                    <div className="msg-sender-info">
                                                        <Avatar name={senderName} photoUrl={senderPhoto} size={22} />
                                                        <span className="msg-sender-name">{senderName}</span>
                                                    </div>
                                                )}
                                                <OfferCard offer={item} isClient={userRole === 'client'} onAccept={acceptOffer} onDecline={declineOffer} />
                                                <span className="msg-bubble-time">{timeAgo(item.created_at)}</span>
                                            </div>
                                        );
                                    }

                                    return (
                                        <div key={item.id} className={`msg-bubble-wrap ${isMine ? 'mine' : 'theirs'}`}>
                                            {!isMine && (
                                                <div className="msg-sender-info">
                                                    <Avatar name={senderName} photoUrl={senderPhoto} size={22} />
                                                    <span className="msg-sender-name">{senderName}</span>
                                                </div>
                                            )}
                                            <div className={`msg-bubble ${isMine ? 'mine' : 'theirs'}`}>
                                                {item.content && <div>{item.content}</div>}
                                                {item.file_url && (
                                                    <a href={item.file_url} target="_blank" rel="noreferrer" className="msg-file-attachment">
                                                        <File size={14} /> {item.file_name || 'Attached file'}
                                                    </a>
                                                )}
                                            </div>
                                            <span className="msg-bubble-time">{timeAgo(item.created_at)}</span>
                                        </div>
                                    );
                                })}
                                <div ref={bottomRef} />
                            </div>

                            {/* Input — pill style */}
                            <div className="msg-input-area">
                                <input type="file" ref={fileInputRef} onChange={handleFileUpload} hidden />
                                <div className="msg-input-pill">
                                    <input
                                        value={text}
                                        onChange={e => setText(e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage(text)}
                                        placeholder={uploading ? 'Uploading...' : 'What do you wanna say?'}
                                        disabled={sending || uploading}
                                    />
                                    <div className="msg-input-bottom">
                                        <button className="msg-action-btn" onClick={() => fileInputRef.current?.click()} disabled={uploading} title="Attach file">
                                            <Paperclip size={16} />
                                        </button>
                                        <button className="msg-send-btn" onClick={() => sendMessage(text)} disabled={sending || !text.trim()}>
                                            <Send size={15} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                                <div className="msg-no-conv">
                                    <div className="msg-no-conv-icon"><MessageSquare size={52} /></div>
                                    <h3>Select a conversation</h3>
                                    <p>Choose from your conversations on the right</p>
                                </div>
                            )}
                        </div>

                        {/* Sidebar - Side Column */}
                        <div className={`analysis-sidebar-col msg-sidebar ${!activeConv ? 'mobile-show' : ''}`}>
                            <div className="msg-sidebar-header">
                                <h2>Recent Chats</h2>
                                <input className="msg-search" placeholder="Search contacts..." value={search} onChange={e => setSearch(e.target.value)} />
                            </div>
                            <div className="msg-list">
                                {filteredConvs.length === 0
                                    ? <div className="msg-empty-sidebar">No conversations yet</div>
                                    : filteredConvs.map(conv => {
                                        const otherId = userRole === 'lawyer' ? conv.client_id : conv.lawyer_id;
                                        const p = getProfile(otherId);
                                        const name = getDisplayName(otherId);
                                        const photo = getPhotoUrl(otherId);
                                        return (
                                            <div key={conv.id}
                                                className={`msg-list-item ${activeConv?.id === conv.id ? 'active' : ''}`}
                                                onClick={() => openConversation(conv)}>
                                                <Avatar name={name} photoUrl={photo} />
                                                <div className="msg-item-info">
                                                    <div className="msg-item-name">{name}</div>
                                                    <div className="msg-item-preview">{getProfile(otherId)?.email || 'Tap to open'}</div>
                                                </div>
                                                <div className="msg-item-meta">
                                                    <span className="msg-item-time">{timeAgo(conv.last_message_at || conv.created_at)}</span>
                                                </div>
                                            </div>
                                        );
                                    })}
                            </div>
                        </div>
                    </div>

                    {/* Offer Modal */}
                    {showOfferModal && (
                        <div className="offer-modal-overlay" onClick={() => setShowOfferModal(false)}>
                            <div className="offer-modal" onClick={e => e.stopPropagation()}>
                                <h3>Send an Offer</h3>
                                <div className="offer-field">
                                    <label>Offer Title *</label>
                                    <input placeholder="e.g. Criminal Defense Consultation" value={offerForm.title}
                                        onChange={e => setOfferForm(p => ({ ...p, title: e.target.value }))} />
                                </div>
                                <div className="offer-field">
                                    <label>Description</label>
                                    <textarea rows={3} placeholder="Describe what's included..." value={offerForm.description}
                                        onChange={e => setOfferForm(p => ({ ...p, description: e.target.value }))} />
                                </div>
                                <div className="offer-field">
                                    <label>Price (PKR) *</label>
                                    <input type="number" placeholder="e.g. 25000" value={offerForm.price}
                                        onChange={e => setOfferForm(p => ({ ...p, price: e.target.value }))} />
                                </div>
                                <div className="offer-modal-btns">
                                    <button className="offer-send-btn" onClick={sendOffer}>Send Offer</button>
                                    <button className="offer-cancel-btn" onClick={() => setShowOfferModal(false)}>Cancel</button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </>
    );
}